import { Classes as BlueprintClasses } from '@blueprintjs/core';
import { Close, Reset } from '@carbon/icons-react';
import * as React from 'react';

import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useParsedChartSpecFromMessage } from 'bones/components/molecules/Voicebox/hooks/useParsedChartSpecFromMessage';
import { VoiceboxChatInput } from 'bones/components/molecules/Voicebox/shared/VoiceboxChatInput';
import { VoiceboxResponseMessage } from 'bones/constants/voicebox/message';
import { Button } from 'bones/design-system/components/Button';
import { Chart } from 'bones/design-system/components/Chart';
import { Modal } from 'bones/design-system/components/Modal';
import { Body } from 'bones/design-system/components/typography/Body';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { getChartCustomizationsKey } from 'bones/utils/voicebox/getChartCustomizationsKey';

export interface VoiceboxPanelChartDialogProps {
  isOpen: boolean;
  message: VoiceboxResponseMessage;
  onClose: () => void;
}

export const VoiceboxPanelChartDialog: React.VFC<
  VoiceboxPanelChartDialogProps
> = ({ isOpen, message, onClose }) => {
  const { copy, testIds } = useVoiceboxSchema();
  const { chartDataByMessageId, requestChart, setChartCustomizations } =
    useVoiceboxPanelData();

  const messageChartData = chartDataByMessageId[message.messageId];
  const initialCustomizations = React.useMemo(
    () =>
      messageChartData?.chartDataByKey[messageChartData?.key || '']
        ?.customizations || /* istanbul ignore next */ [],
    [messageChartData]
  );

  const [localCustomizations, setLocalCustomizations] = React.useState<
    string[]
  >(initialCustomizations);

  const didIsOpenChange = useDidValueChange(isOpen);
  React.useEffect(() => {
    if (didIsOpenChange && isOpen) {
      setLocalCustomizations(initialCustomizations);
    }
  }, [didIsOpenChange, isOpen, initialCustomizations]);

  const chartData =
    messageChartData?.chartDataByKey[
      getChartCustomizationsKey(localCustomizations)
    ];
  const { error: chartError, spec: chartSpec } = useParsedChartSpecFromMessage({
    actions: chartData?.data || undefined,
  });
  const [hasValidChart, setHasValidChart] = React.useState<boolean | undefined>(
    undefined
  );

  const [query, setQuery] = React.useState<string>('');

  const addCustomization = React.useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      return;
    }

    const nextCustomizations = localCustomizations.concat([trimmed]);
    setLocalCustomizations(nextCustomizations);
    requestChart(message.messageId, nextCustomizations);
    setQuery('');
  }, [localCustomizations, message, query, requestChart]);

  const removeCustomization = React.useCallback(
    (customizationIdx: number) => {
      const nextCustomizations = localCustomizations.slice(0);
      nextCustomizations.splice(customizationIdx, 1);
      setLocalCustomizations(nextCustomizations);
      requestChart(message.messageId, nextCustomizations);
    },
    [localCustomizations, message, requestChart]
  );

  return (
    <Modal
      bodyTestId={testIds.panel.messageSummary.customizeChart.body}
      className="vb-voicebox-panel-chart-dialog"
      footerActions={
        <>
          <Button
            onClick={() => {
              onClose();
            }}
            size="sm"
            testId={testIds.panel.messageSummary.customizeChart.cancel}
            variant="secondary2-gray"
          >
            {copy.panel.messageSummary.customizeChart.cancel}
          </Button>
          <Button
            disabled={!chartData?.data || !hasValidChart}
            onClick={() => {
              setChartCustomizations(message.messageId, localCustomizations);
              onClose();
            }}
            size="sm"
            testId={testIds.panel.messageSummary.customizeChart.submit}
            variant="primary-accent"
          >
            {copy.panel.messageSummary.customizeChart.submit}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={onClose}
      title={copy.panel.messageSummary.customizeChart.title}
    >
      <div className="vb-voicebox-panel-chart">
        {chartData?.loading ? (
          <div
            className={BlueprintClasses.SKELETON}
            data-testid={testIds.panel.messageSummary.customizeChart.loading}
          />
        ) : (
          <>
            {(chartData && !chartData.data) ||
            chartError ||
            hasValidChart === false ? (
              <div className="vb-voicebox-panel-chart-error">
                {copy.panel.messageSummary.customizeChart.error}
                <Button
                  leftIcon={Reset}
                  onClick={() => {
                    requestChart(message.messageId, localCustomizations, true);
                  }}
                  size="sm"
                  testId={testIds.panel.messageSummary.customizeChart.retry}
                  variant="tertiary-gray"
                >
                  {copy.panel.messageSummary.customizeChart.retry}
                </Button>
              </div>
            ) : null}

            {chartSpec ? (
              <Chart
                onValidateChart={setHasValidChart}
                showActions={false}
                spec={chartSpec}
                testId={testIds.panel.messageSummary.customizeChart.chart(
                  getChartCustomizationsKey(localCustomizations),
                  localCustomizations.length
                )}
              />
            ) : null}
          </>
        )}
      </div>

      {localCustomizations.length ? (
        <div className="vb-voicebox-panel-chart-customizations">
          <Body
            className="vb-voicebox-panel-chart-customizations-title"
            size="lg"
            weight="semibold"
          >
            {copy.panel.messageSummary.customizeChart.customizations}
          </Body>

          {localCustomizations.map((customization, customizationIdx) => {
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={customizationIdx}
                className="vb-voicebox-panel-chart-customization"
              >
                <Body size="md">{customization}</Body>
                <Button
                  leftIcon={Close}
                  onClick={() => {
                    removeCustomization(customizationIdx);
                  }}
                  size="xs"
                  testId={testIds.panel.messageSummary.customizeChart.remove(
                    customization,
                    customizationIdx
                  )}
                  variant="ghost-gray"
                />
              </div>
            );
          })}
        </div>
      ) : null}

      <VoiceboxChatInput
        autoFocus
        inputTestId={testIds.panel.messageSummary.customizeChart.input}
        loading={chartData?.loading}
        onChange={(evt) => {
          setQuery(evt.target.value);
        }}
        onClickSend={() => {
          addCustomization();
        }}
        onKeyDown={(evt) => {
          if (evt.key === 'Enter') {
            evt.preventDefault();
            addCustomization();
          }
        }}
        placeholder={copy.drawer.placeholder}
        sendTestId={testIds.panel.messageSummary.customizeChart.send}
        value={query}
      />
    </Modal>
  );
};
